import { type ComponentType, type PropsWithChildren, type ReactElement } from 'react'
import { ParcelTapeIllustration, type SVGIllustrationProps } from '@dhl-ui/illustrations'
import type { BaseProps, MinimalSxPadding } from '../../types'
import { spacing } from '../../theme'
import { Box } from '../Box'
import { Body1, H5 } from '../Typography'
import { StackColumn } from '../Stack'
import { IconSeverity, type IconSeverityProps } from '../IconSeverity'

type BlazeSlotProps = {
  illustration?: {
    variant?: ComponentType
  } & Partial<SVGIllustrationProps>
  iconSeverity?: {
    variant: ComponentType
  } & Omit<IconSeverityProps, 'children'>
}

type InnerProps = PropsWithChildren<{
  slotProps?: BlazeSlotProps
}> &
  BaseProps<HTMLElement, MinimalSxPadding | 'maxWidth'>

export type BlazeProps = InnerProps &
  ({ title: string; description?: string | ReactElement } | { title?: string; description: string | ReactElement })

/**
 * Blaze
 *
 * Demos:
 * - [Blaze](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/primitives-blaze--docs)
 *
 */

export const Blaze = ({ children, slotProps, title, description, sx, ...props }: BlazeProps) => {
  const { variant: IllustrationVariant = ParcelTapeIllustration, ...illustrationProps } = slotProps?.illustration ?? {}

  const resolvedNode = slotProps?.iconSeverity ? (
    (({ variant, ...iconProps }) => {
      const IconComponent = variant

      return (
        <IconSeverity {...iconProps}>
          <IconComponent />
        </IconSeverity>
      )
    })(slotProps.iconSeverity)
  ) : (
    <IllustrationVariant size="M" {...illustrationProps} />
  )

  return (
    <Box
      {...props}
      sx={{
        ...sx,
        p: spacing(0, 2),
        maxWidth: '480px',
      }}
    >
      <StackColumn gap={3} sx={{ textAlign: 'center', display: 'flex', alignItems: 'center' }}>
        <Box sx={{ display: 'flex' }}>{resolvedNode}</Box>
        <StackColumn gap={0.5}>
          <H5>{title}</H5>
          {description && <Body1>{description}</Body1>}
        </StackColumn>
        {children}
      </StackColumn>
    </Box>
  )
}
